import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { 
    CheckCircle2, 
    ExternalLink, 
    Download, 
    AlertCircle,
    Search,
    ArrowRight,
    Award,
    X,
    Copy,
    Sun,
    Moon,
    ZoomIn,
    Share2,
    Sparkles,
    Check,
    Image as ImageIcon,
    Loader2
} from 'lucide-react';
import { Images } from '@/assets';
import verificationService, { type PublicVerificationData } from '@/services/verification/verificationService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PdfThumbnail from './PdfThumbnail';
import VerificationSplashScreen from './VerificationSplashScreen';
import './VerifyCertificate.css';

const getFileUrl = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const baseUrl = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050/api/v1').replace('/api/v1', '');
    return `${baseUrl}/${path.startsWith('/') ? path.slice(1) : path}`;
};

const getLevel = (score: number): string => {
    if (score >= 85) return 'Advanced';
    if (score >= 70) return 'Proficient';
    if (score >= 50) return 'Developing';
    return 'Beginner';
};

export const formatScore = (val: number | null | undefined): string => {
    if (val == null || isNaN(val)) return '0';
    const rounded = Math.round((val + Number.EPSILON) * 10) / 10;
    return rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1);
};

interface SkillItem {
    key: string;
    label: string;
    score: number;
    color: string;
}

const VerifyCertificatePage: React.FC = () => {
    const { id: paramId } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const certId = paramId || searchParams.get('id') || searchParams.get('cert') || '';

    const [data, setData] = useState<PublicVerificationData | null>(null);
    const [loading, setLoading] = useState(false);
    const [showSplash, setShowSplash] = useState<boolean>(() => Boolean(certId));
    const [error, setError] = useState<string | null>(null);
    const [inputQuery, setInputQuery] = useState(certId);
    const [certImgFailed, setCertImgFailed] = useState(false);

    // Theme state: light | dark
    const [theme, setTheme] = useState<'light' | 'dark'>(() => {
        try {
            const saved = localStorage.getItem('tpa-theme');
            if (saved === 'light' || saved === 'dark') return saved;
        } catch {
            // fallback
        }
        if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        return 'light';
    });

    const toggleTheme = () => {
        const next = theme === 'dark' ? 'light' : 'dark';
        setTheme(next);
        try {
            localStorage.setItem('tpa-theme', next);
        } catch {
            // ignore
        }
    };

    // Keep data-theme synced
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
    }, [theme]);

    // QR scan banner visibility
    const [showScan, setShowScan] = useState(() => searchParams.get('src') === 'qr');
    const [flashCert, setFlashCert] = useState(false);

    // Modal state for certificate
    const [modalOpen, setModalOpen] = useState(false);
    // Share modal state
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [linkedInPostContent, setLinkedInPostContent] = useState<string | null>(null);
    const [loadingLinkedInPost, setLoadingLinkedInPost] = useState(false);
    const [captionCopied, setCaptionCopied] = useState(false);
    const [downloadingScreenshot, setDownloadingScreenshot] = useState(false);

    // Esc key to close modal & scroll lock
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && modalOpen) {
                setModalOpen(false);
            }
        };
        if (modalOpen) {
            window.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
                document.body.style.overflow = '';
            };
        }
    }, [modalOpen]);

    // Floating toast state
    const [toastMsg, setToastMsg] = useState<string | null>(null);
    const toastTimerRef = useRef<NodeJS.Timeout | null>(null);

    const showToast = (msg: string) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToastMsg(msg);
        toastTimerRef.current = setTimeout(() => {
            setToastMsg(null);
        }, 1800);
    };

    const copyToClipboard = async (text: string, successMsg: string) => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
                showToast(successMsg);
            } else {
                showToast('Copy is not available in this browser');
            }
        } catch {
            showToast('Failed to copy to clipboard');
        }
    };

    const performVerification = async (identifier: string) => {
        const trimmed = identifier.trim();
        if (!trimmed) {
            setError('Please enter a valid Certificate ID or Enrollment Number.');
            return;
        }

        try {
            setLoading(true);
            setShowSplash(true);
            setError(null);
            const res = await verificationService.getVerification(trimmed);
            setData(res);
        } catch (err: any) {
            setData(null);
            setError(err.response?.data?.message || `Certificate verification failed. No official record found for "${trimmed}".`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (certId) {
            setInputQuery(certId);
            performVerification(certId);
        } else {
            setData(null);
            setError(null);
            setLoading(false);
        }
    }, [certId]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = inputQuery.trim();
        if (trimmed) {
            navigate(`/verify/${encodeURIComponent(trimmed)}`);
        }
    };

    const cleanCohortName = (name?: string | null): string => {
        if (!name) return 'August 2026';
        let cleaned = name.trim();
        cleaned = cleaned.replace(/^(.+?)\s*\(\1\)$/i, '$1').trim();
        cleaned = cleaned.replace(/\(([^)]+)\)\s*\(\1\)/gi, '($1)').trim();
        const match = cleaned.match(/^(.+?)\s*\((.+?)\)$/i);
        if (match && match[1].trim().toLowerCase() === match[2].trim().toLowerCase()) {
            cleaned = match[1].trim();
        }
        return cleaned || 'August 2026';
    };

    const cleanProjectTitle = (title?: string | null): string => {
        if (!title) return 'Capstone Project Overview';
        let cleaned = title.trim();
        cleaned = cleaned.replace(/\(\s+/g, '(').replace(/\[\s+/g, '[');
        cleaned = cleaned.replace(/\s+\)/g, ')').replace(/\s+\]/g, ']');
        cleaned = cleaned.replace(/\s+([,.:;!?])/g, '$1');
        cleaned = cleaned.replace(/\s{2,}/g, ' ');
        return cleaned.trim() || 'Capstone Project Overview';
    };

    const formattedDate = (dateStr?: string | null) => {
        if (!dateStr) return '15 September 2026';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        } catch {
            return dateStr;
        }
    };

    const openInNewTab = (path?: string | null) => {
        if (!path) return;
        const url = getFileUrl(path);
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    // Helper for initials
    const getInitials = (name?: string) => {
        if (!name) return 'FP';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        return name.slice(0, 2).toUpperCase();
    };

    // Dynamic Skill calculation from data.skill_gains
    // Converts 1.0 - 5.0 scale to 0 - 100 percentage
    const skills: SkillItem[] = useMemo(() => {
        const gains = data?.skill_gains;
        const toPercent = (val: number | undefined | null): number => {
            if (val == null || isNaN(val) || val <= 0) return 0;
            const pct = val > 5 ? Math.min(100, val) : Math.min(100, Math.max(0, (val / 5) * 100));
            return Math.round((pct + Number.EPSILON) * 10) / 10;
        };

        return [
            {
                key: 'presentation',
                label: 'Presentation',
                score: toPercent(gains?.presentation),
                color: '#B600DE',
            },
            {
                key: 'problem_identification',
                label: 'Problem identification',
                score: toPercent(gains?.problem_identification),
                color: '#0090D8',
            },
            {
                key: 'problem_solving',
                label: 'Problem solving',
                score: toPercent(gains?.problem_solving),
                color: '#00B26F',
            },
            {
                key: 'coding_skill',
                label: 'Coding skill',
                score: toPercent(gains?.coding_skill),
                color: '#FF8000',
            },
            {
                key: 'journey_score',
                label: 'Growth and learning',
                score: toPercent(gains?.journey_score),
                color: '#F11B1B',
            },
        ];
    }, [data?.skill_gains]);

    // Calculate overall average score out of 100
    const overallScore = useMemo(() => {
        if (!skills || skills.length === 0) return 0;
        const nonZero = skills.filter((s) => s.score > 0);
        if (nonZero.length === 0) return 0;
        const avg = nonZero.reduce((acc, s) => acc + s.score, 0) / nonZero.length;
        return Math.round((avg + Number.EPSILON) * 10) / 10;
    }, [skills]);

    // Animated count-up score
    const [displayScore, setDisplayScore] = useState(0);
    useEffect(() => {
        if (overallScore <= 0) {
            setDisplayScore(0);
            return;
        }
        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (prefersReducedMotion) {
            setDisplayScore(overallScore);
            return;
        }

        let startTime: number | null = null;
        const duration = 1400;
        let rafId: number;

        const tick = (timestamp: number) => {
            if (startTime === null) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            const easeOutCubic = 1 - Math.pow(1 - progress, 3);
            if (progress >= 1) {
                setDisplayScore(overallScore);
            } else {
                const current = overallScore * easeOutCubic;
                setDisplayScore(Math.round((current + Number.EPSILON) * 10) / 10);
            }
            if (progress < 1) {
                rafId = requestAnimationFrame(tick);
            }
        };

        const timer = setTimeout(() => {
            rafId = requestAnimationFrame(tick);
        }, 1000);

        return () => {
            clearTimeout(timer);
            cancelAnimationFrame(rafId);
        };
    }, [overallScore]);

    // Fill bars animation
    const [barsFilled, setBarsFilled] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setBarsFilled(true), 400);
        return () => clearTimeout(t);
    }, []);

    // 3D Tilt for Verified Seal
    const heroRef = useRef<HTMLElement>(null);
    const sealRef = useRef<HTMLDivElement>(null);

    const handlePointerMove = (e: React.PointerEvent<HTMLElement>) => {
        if (!heroRef.current || !sealRef.current) return;
        const heroRect = heroRef.current.getBoundingClientRect();
        const sealRect = sealRef.current.getBoundingClientRect();
        const dx = (e.clientX - (sealRect.left + sealRect.width / 2)) / heroRect.width;
        const dy = (e.clientY - (sealRect.top + sealRect.height / 2)) / heroRect.height;
        sealRef.current.style.setProperty('--ty', `${(dx * 28).toFixed(2)}deg`);
        sealRef.current.style.setProperty('--tx', `${(-dy * 28).toFixed(2)}deg`);
    };

    const handlePointerLeave = () => {
        if (!sealRef.current) return;
        sealRef.current.style.setProperty('--tx', '0deg');
        sealRef.current.style.setProperty('--ty', '0deg');
    };

    // Hovered skill index to highlight spoke on the radar chart
    const [hoveredSkillIndex, setHoveredSkillIndex] = useState<number | null>(null);

    // Scroll down to certificate with flash animation
    const handleViewCertClick = (e: React.MouseEvent) => {
        e.preventDefault();
        const el = document.getElementById('certificate');
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setFlashCert(false);
            requestAnimationFrame(() => {
                setFlashCert(true);
            });
        }
    };

    // LinkedIn certification share handler
    const handleAddToLinkedIn = () => {
        if (!data) return;
        const date = new Date(data.issued_at || data.completion_date || Date.now());
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const certUrl = window.location.href;
        const url = `https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME&name=${encodeURIComponent('AI Impact Fellowship')}&organizationName=${encodeURIComponent('The Purpose Academy (TPA)')}&issueYear=${year}&issueMonth=${month}&certUrl=${encodeURIComponent(certUrl)}&certId=${encodeURIComponent(data.certificate_number)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleOpenShareModal = async () => {
        setShareModalOpen(true);
        if (!linkedInPostContent && data?.id) {
            try {
                setLoadingLinkedInPost(true);
                const res = await verificationService.getCertificateLinkedInPost(data.id || data.enrollment_number);
                if (res?.linkedin_post_content) {
                    setLinkedInPostContent(res.linkedin_post_content);
                }
            } catch (err) {
                console.error('Failed to generate LinkedIn post:', err);
            } finally {
                setLoadingLinkedInPost(false);
            }
        }
    };

    const getSuggestedCaption = () => {
        if (linkedInPostContent) return linkedInPostContent;
        if (!data) return '';
        const projectText = data.project?.project_title ? ` During the program, I developed my capstone project: "${cleanProjectTitle(data.project.project_title)}".` : '';
        return `🎓 Proud to share that I completed the AI Fellowship Program and presented my project on 12/9/2026 for this cohort!${projectText}\n\nCheck out my official verified credential and project showcase here:\n${window.location.href}\n\nThis incredible journey wouldn't have been possible without the guidance and support of amazing leaders, Manav Subodh and Parth Singh. I want to express my deepest gratitude to The Purpose Academy (TPA) for providing such a transformative fellowship experience that empowers students to solve real-world problems.\n\n@Parth Singh @Manav Subodh\n\n#ThePurposeAcademy #TPA #AIFellowship #ArtificialIntelligence #Innovation #StudentInnovators`;
    };

    const handleCopyCaption = () => {
        const text = getSuggestedCaption();
        copyToClipboard(text, 'LinkedIn post caption copied to clipboard!');
        setCaptionCopied(true);
        setTimeout(() => setCaptionCopied(false), 2500);
    };

    const handleShareLinkedInPost = () => {
        const text = getSuggestedCaption();
        copyToClipboard(text, 'Caption & mentions copied! Paste (Ctrl+V) into your LinkedIn post.');
        window.open('https://www.linkedin.com/feed/?shareActive=true', '_blank', 'noopener,noreferrer');
    };

    const handleDownloadPresentationScreenshot = async () => {
        if (!data?.project?.project_screenshot_path) return;
        const sanitizedName = (data.student_name || 'student').replace(/[^a-zA-Z0-9_-]/g, '_');
        const fileName = `${sanitizedName}_Demo_Screenshot.png`;
        try {
            setDownloadingScreenshot(true);
            const imgUrl = getFileUrl(data.project.project_screenshot_path);
            const response = await fetch(imgUrl);
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
            showToast('Presentation screenshot downloaded!');
        } catch {
            showToast('Failed to download presentation screenshot');
        } finally {
            setDownloadingScreenshot(false);
        }
    };

    // Close modal on Escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && modalOpen) {
                setModalOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [modalOpen]);

    // Geometry for Radar Chart
    const R = 118;
    const n = skills.length; // 5
    const pt = (i: number, r: number): [number, number] => {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        return [Math.cos(a) * r, Math.sin(a) * r];
    };
    const poly = (f: number): string =>
        skills.map((_, i) => pt(i, R * f).map((v) => v.toFixed(2)).join(',')).join(' ');

    const shapePoints = skills
        .map((s, i) => pt(i, (R * Math.max(s.score, 5)) / 100).map((v) => v.toFixed(2)).join(','))
        .join(' ');

    return (
        <>
            {showSplash && (
                <VerificationSplashScreen
                    isReady={!loading}
                    hasError={Boolean(error)}
                    onFinish={() => setShowSplash(false)}
                />
            )}

            <div className="tpa-profile" data-theme={theme}>
                {/* Cinematic Letterbox Bars */}
                <div className="letterbox top" aria-hidden="true" />
                <div className="letterbox bottom" aria-hidden="true" />

                {/* ================= TOP BAR ================= */}
                <header className="topbar">
                    <div className="wrap">
                        <Link to="/" className="brand" aria-label="The Purpose Academy home">
                            <img 
                                src={Images.tpaRealLogo} 
                                alt="The Purpose Academy" 
                            />
                            <div className="brand-text">
                                <strong>The Purpose Academy</strong>
                                <span>AI Impact Fellowship</span>
                            </div>
                        </Link>

                        <div className="top-actions">
                            <button 
                                className="theme-switch" 
                                id="themeSwitch" 
                                onClick={toggleTheme}
                                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
                                title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
                            >
                                <span className="knob" />
                                <Sun className="sun" />
                                <Moon className="moon" />
                            </button>
                        </div>
                    </div>
                </header>

                <main className="wrap">
                    {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16 }}>
                            <div style={{ width: 44, height: 44, border: '4px solid var(--line)', borderTopColor: 'var(--text)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            <p style={{ fontSize: 14, color: 'var(--text-3)' }}>Verifying fellowship credentials...</p>
                        </div>
                    ) : !data ? (
                        /* Search / Verification Fallback View */
                        <div style={{ maxWidth: 640, margin: '48px auto', padding: '0 16px' }}>
                            <div className="panel" style={{ textAlign: 'center', padding: '40px 32px' }}>
                                <div style={{ width: 64, height: 64, borderRadius: 20, background: 'var(--ok-bg)', color: 'var(--ok)', display: 'grid', placeItems: 'center', margin: '0 auto 20px', border: '1px solid var(--line)' }}>
                                    <Award size={32} />
                                </div>
                                <h1 style={{ fontSize: 26, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                                    Official Credential Verification
                                </h1>
                                <p style={{ fontSize: 14.5, color: 'var(--text-2)', maxWidth: 440, margin: '0 auto 24px', lineHeight: 1.5 }}>
                                    Verify the authenticity of an AI Impact Fellowship Certificate of Completion and inspect the fellow's verified skill profile.
                                </p>

                                <form onSubmit={handleSearchSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 440, margin: '0 auto' }}>
                                    <div style={{ position: 'relative' }}>
                                        <div style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}>
                                            <Search size={18} />
                                        </div>
                                        <input
                                            type="text"
                                            value={inputQuery}
                                            onChange={(e) => setInputQuery(e.target.value)}
                                            placeholder="Enter Certificate ID (e.g. 26-Aug-001)"
                                            style={{
                                                width: '100%',
                                                padding: '12px 100px 12px 42px',
                                                borderRadius: 14,
                                                border: '1px solid var(--line)',
                                                background: 'var(--surface-2)',
                                                color: 'var(--text)',
                                                fontSize: 14,
                                                outline: 'none',
                                            }}
                                            autoFocus
                                        />
                                        <button
                                            type="submit"
                                            disabled={!inputQuery.trim()}
                                            className="btn btn-primary"
                                            style={{
                                                position: 'absolute',
                                                right: 6,
                                                top: 6,
                                                bottom: 6,
                                                height: 'auto',
                                                padding: '0 16px',
                                                fontSize: 13,
                                                borderRadius: 10,
                                            }}
                                        >
                                            <span>Verify</span>
                                            <ArrowRight size={14} />
                                        </button>
                                    </div>

                                    {error && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 12, borderRadius: 12, background: 'rgba(241, 27, 27, 0.08)', border: '1px solid rgba(241, 27, 27, 0.25)', color: 'var(--tpa-red)', fontSize: 13.5, textAlign: 'left' }}>
                                            <AlertCircle size={18} style={{ flexShrink: 0 }} />
                                            <span>{error}</span>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-3)', marginTop: 8 }}>
                                        <span>Sample ID:</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setInputQuery('26-Aug-001');
                                                navigate('/verify/26-Aug-001');
                                            }}
                                            className="chip"
                                            style={{ cursor: 'pointer', background: 'var(--surface-3)', border: 'none' }}
                                        >
                                            26-Aug-001
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* ================= QR SCAN RESULT ================= */}
                            {showScan && (
                                <section className="scan" id="scan" aria-live="polite">
                                    <span className="scan-mark">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 6L9 17l-5-5"/>
                                        </svg>
                                    </span>
                                    <p className="scan-copy">
                                        <b>Genuine certificate</b>
                                        <span>Issued to {data.student_name} on {formattedDate(data.issued_at || data.completion_date)}, ID {data.certificate_number}</span>
                                    </p>
                                    <a className="scan-link" href="#certificate" onClick={handleViewCertClick}>
                                        View<span className="hide-sm"> certificate</span>
                                    </a>
                                    <button 
                                        className="scan-close" 
                                        onClick={() => setShowScan(false)} 
                                        aria-label="Dismiss banner"
                                    >
                                        <X size={16} />
                                    </button>
                                </section>
                            )}

                            {/* ================= HERO ================= */}
                            <section 
                                className="hero" 
                                ref={heroRef}
                                onPointerMove={handlePointerMove}
                                onPointerLeave={handlePointerLeave}
                                aria-label="Fellow credential"
                            >
                                <div className="orbs" aria-hidden="true">
                                    <i /><i /><i />
                                </div>
                                <div className="hero-inner">
                                    <div>
                                        <div className="hello">
                                            {data.profile_picture ? (
                                                <img 
                                                    src={getFileUrl(data.profile_picture)} 
                                                    alt={data.student_name} 
                                                    className="avatar"
                                                />
                                            ) : (
                                                <div className="avatar" aria-hidden="true">
                                                    {getInitials(data.student_name)}
                                                </div>
                                            )}
                                            <p>
                                                <b>Skills profile</b>
                                                <span>AI Impact Fellowship, {cleanCohortName(data.cohort_name)} cohort</span>
                                            </p>
                                        </div>

                                        {/* Fellow Name with animated masked letters */}
                                        <h1 id="fellowName" aria-label={data.student_name}>
                                            {[...data.student_name].map((char, idx) => (
                                                <span key={idx} className="ch" aria-hidden="true">
                                                    <span style={{ animationDelay: `${0.95 + idx * 0.06}s` }}>
                                                        {char === ' ' ? '\u00A0' : char}
                                                    </span>
                                                </span>
                                            ))}
                                        </h1>

                                        {/* Name strokes underline */}
                                        <div className="name-strokes" aria-hidden="true">
                                            <i style={{ flex: 3, background: 'var(--tpa-green)' }} />
                                            <i style={{ flex: 1.2, background: 'var(--tpa-red)' }} />
                                            <i style={{ flex: 2, background: 'var(--tpa-orange)' }} />
                                            <i style={{ flex: 2.6, background: 'var(--tpa-purple)' }} />
                                            <i style={{ flex: 2.2, background: 'var(--tpa-blue)' }} />
                                        </div>

                                        <p className="lede">
                                            Completed the <b>AI Impact Fellowship</b>, a four-week mentor-guided program, and presented a capstone solution at Demo Day.
                                        </p>

                                        <div className="hero-actions">
                                            {data.certificate_url && (
                                                <a 
                                                    className="btn btn-primary" 
                                                    href={getFileUrl(data.certificate_url)} 
                                                    target="_blank" 
                                                    rel="noreferrer" 
                                                    download
                                                >
                                                    <Download size={16} />
                                                    Download certificate
                                                </a>
                                            )}
                                            <button 
                                                className="btn btn-ghost" 
                                                onClick={handleAddToLinkedIn}
                                                title="Add certificate to LinkedIn profile"
                                            >
                                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                                    <path d="M4.98 3.5a2.5 2.5 0 11-.01 5 2.5 2.5 0 01.01-5zM3 9.5h4V21H3zM9.5 9.5h3.8v1.6h.1c.5-1 1.8-2 3.8-2 4 0 4.8 2.6 4.8 6V21h-4v-5.2c0-1.2 0-2.8-1.7-2.8s-2 1.3-2 2.7V21h-4z"/>
                                                </svg>
                                                Add to LinkedIn
                                            </button>
                                            <button 
                                                className="btn btn-ghost" 
                                                onClick={handleOpenShareModal}
                                                title="Share achievement to LinkedIn"
                                            >
                                                <Share2 size={16} />
                                                Share to LinkedIn
                                            </button>
                                            <button 
                                                className="btn btn-ghost" 
                                                onClick={() => copyToClipboard(window.location.href, 'Profile link copied')}
                                            >
                                                <Copy size={16} />
                                                Copy profile link
                                            </button>
                                        </div>
                                    </div>

                                    {/* Verified Seal */}
                                    <div className="seal-stage">
                                        <div className="seal" ref={sealRef} aria-label="Overall fellowship score">
                                            <div className="ring blur" />
                                            <div className="ring" />
                                            <svg className="ticks" viewBox="-100 -100 200 200" aria-hidden="true" id="ticks">
                                                {Array.from({ length: 72 }, (_, i) => {
                                                    const a = (i * 5 * Math.PI) / 180;
                                                    const major = i % 18 === 0;
                                                    const r1 = major ? 88 : 93;
                                                    const r2 = 99;
                                                    return (
                                                        <line
                                                            key={i}
                                                            x1={Math.cos(a) * r1}
                                                            y1={Math.sin(a) * r1}
                                                            x2={Math.cos(a) * r2}
                                                            y2={Math.sin(a) * r2}
                                                            stroke={major ? 'var(--text)' : 'var(--text-3)'}
                                                            strokeWidth={major ? 2 : 1}
                                                            strokeLinecap="round"
                                                        />
                                                    );
                                                })}
                                            </svg>
                                            <div className="core">
                                                <span className="score" id="overall">{formatScore(displayScore)}</span>
                                                <span className="of">Overall, out of 100</span>
                                                <span className="lvl" id="overallLvl">
                                                    <CheckCircle2 size={12} />
                                                    <span>{getLevel(overallScore)}</span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Credential Facts Strip */}
                                <dl className="facts">
                                    <div className="fact">
                                        <dt><small>Program</small></dt>
                                        <dd><b>AI Impact Fellowship</b></dd>
                                    </div>
                                    <div className="fact">
                                        <dt><small>Cohort</small></dt>
                                        <dd><b>{cleanCohortName(data.cohort_name)}</b></dd>
                                    </div>
                                    <div className="fact">
                                        <dt><small>Completed on</small></dt>
                                        <dd><b>{formattedDate(data.completion_date || data.issued_at)}</b></dd>
                                    </div>
                                    <div className="fact">
                                        <dt><small>Certificate ID</small></dt>
                                        <dd>
                                            <b>
                                                {data.certificate_number}
                                                <button 
                                                    className="copy" 
                                                    onClick={() => copyToClipboard(data.certificate_number, 'Credential ID copied')}
                                                    aria-label="Copy certificate ID"
                                                    title="Copy certificate ID"
                                                >
                                                    <Copy size={12} />
                                                </button>
                                            </b>
                                        </dd>
                                    </div>
                                </dl>
                            </section>

                            {/* ================= SKILLS PANEL ================= */}
                            <section className="panel skills-panel reveal in" aria-labelledby="skillHead">
                                <div className="panel-head">
                                    <div>
                                        <h2 id="skillHead">Skill profile</h2>
                                        <p>Five competencies scored by mentors during the fellowship</p>
                                    </div>
                                </div>

                                <div className="skills">
                                    {/* Radar Chart SVG */}
                                    <svg 
                                        className="radar" 
                                        id="radar" 
                                        viewBox="-215 -170 430 345" 
                                        role="img" 
                                        aria-label="Competency Radar Chart"
                                    >
                                        {/* Radial concentric polygon rings */}
                                        {[0.25, 0.5, 0.75, 1].map((f) => (
                                            <polygon key={f} className="ring" points={poly(f)} />
                                        ))}

                                        {/* Axis dashed lines */}
                                        {skills.map((_, i) => {
                                            const [x, y] = pt(i, R);
                                            return <line key={i} className="axis" x1={0} y1={0} x2={x} y2={y} />;
                                        })}

                                        {/* Filled competency polygon */}
                                        <polygon className="shape" points={shapePoints} />

                                        {/* Spokes, glow, node points & text labels */}
                                        {skills.map((s, i) => {
                                            const [x, y] = pt(i, (R * Math.max(s.score, 5)) / 100);
                                            const len = Math.hypot(x, y);
                                            const isDimmed = hoveredSkillIndex !== null && hoveredSkillIndex !== i;
                                            const [lx, ly] = pt(i, R + 24);
                                            const anchor = Math.abs(lx) < 8 ? 'middle' : lx > 0 ? 'start' : 'end';
                                            const dy = ly < -R ? -14 : 0;
                                            const sp1 = s.label.indexOf(' ');
                                            const lines = s.label.length > 13 && sp1 > 0 ? [s.label.slice(0, sp1), s.label.slice(sp1 + 1)] : [s.label];
                                            const top = ly + dy - (lines.length - 1) * 7;

                                            return (
                                                <g key={s.key}>
                                                    <line 
                                                        className="spoke-glow" 
                                                        x1={0} 
                                                        y1={0} 
                                                        x2={x} 
                                                        y2={y} 
                                                        stroke={s.color} 
                                                        style={{ opacity: isDimmed ? 0.1 : undefined }}
                                                    />
                                                    <line 
                                                        className="spoke" 
                                                        x1={0} 
                                                        y1={0} 
                                                        x2={x} 
                                                        y2={y} 
                                                        stroke={s.color} 
                                                        strokeDasharray={len} 
                                                        style={{ 
                                                            ['--len' as any]: len, 
                                                            animationDelay: `${0.3 + i * 0.08}s`,
                                                            opacity: isDimmed ? 0.15 : 1
                                                        }} 
                                                    />
                                                    <circle 
                                                        className="node" 
                                                        cx={x} 
                                                        cy={y} 
                                                        r={7} 
                                                        stroke={s.color} 
                                                        style={{ animationDelay: `${0.9 + i * 0.08}s` }} 
                                                    />

                                                    <text x={lx} y={top} textAnchor={anchor} dominantBaseline="middle">
                                                        {lines.map((ln, k) => (
                                                            <tspan key={k} x={lx} dy={k ? 14 : 0}>
                                                                {ln}
                                                            </tspan>
                                                        ))}
                                                    </text>
                                                    <text 
                                                        className="val" 
                                                        x={lx} 
                                                        y={top + (lines.length - 1) * 14 + 16} 
                                                        textAnchor={anchor} 
                                                        dominantBaseline="middle"
                                                    >
                                                        {formatScore(s.score)}
                                                    </text>
                                                </g>
                                            );
                                        })}

                                        {/* Center Hub */}
                                        <circle className="hub" cx={0} cy={0} r={9} />
                                    </svg>

                                    {/* Skill List */}
                                    <ul className="skill-list" id="skillList">
                                        {skills.map((s, i) => (
                                            <li 
                                                key={s.key} 
                                                className={`skill ${hoveredSkillIndex === i ? 'is-active' : ''}`}
                                                tabIndex={0}
                                                onMouseEnter={() => setHoveredSkillIndex(i)}
                                                onMouseLeave={() => setHoveredSkillIndex(null)}
                                                onFocus={() => setHoveredSkillIndex(i)}
                                                onBlur={() => setHoveredSkillIndex(null)}
                                            >
                                                <div className="skill-top">
                                                    <span className="skill-name">
                                                        <i style={{ background: s.color }} />
                                                        {s.label}
                                                    </span>
                                                    <span className="skill-score">
                                                        <small>{getLevel(s.score)}</small>
                                                        {formatScore(s.score)}
                                                    </span>
                                                </div>
                                                <span className="track">
                                                    <span 
                                                        className="fill" 
                                                        style={{ 
                                                            background: s.color, 
                                                            color: s.color, 
                                                            width: barsFilled ? `${s.score}%` : '0%' 
                                                        }} 
                                                    />
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </section>

                            {/* ================= CERTIFICATE + CAPSTONE PROJECT ================= */}
                            <div className="grid">
                                {/* Certificate Panel */}
                                <section 
                                    className={`panel cert-panel reveal in ${flashCert ? 'flash' : ''}`} 
                                    id="certificate" 
                                    aria-labelledby="certHead"
                                >
                                    <div className="panel-head">
                                        <div>
                                            <h2 id="certHead">Certificate</h2>
                                            <p>The QR code on this certificate opens this page</p>
                                        </div>
                                    </div>

                                    <div 
                                        className="cert-frame" 
                                        id="certOpen" 
                                        role="button" 
                                        tabIndex={0} 
                                        aria-label="Open certificate preview"
                                        onClick={() => setModalOpen(true)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setModalOpen(true);
                                            }
                                        }}
                                    >
                                        <div className="cert-preview-wrapper">
                                            {!certImgFailed && data.certificate_image_url ? (
                                                <img
                                                    src={getFileUrl(data.certificate_image_url)}
                                                    alt="Certificate of Completion"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    onError={() => {
                                                        if (data.id && !data.certificate_image_url?.includes('/preview')) {
                                                            const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050/api/v1').replace(/\/+$/, '');
                                                            const previewUrl = `${apiBase}/admin/students/public/certificate/${data.id}/preview`;
                                                            setData(prev => prev ? { ...prev, certificate_image_url: previewUrl } : null);
                                                        } else {
                                                            setCertImgFailed(true);
                                                        }
                                                    }}
                                                />
                                            ) : data.certificate_url ? (
                                                <PdfThumbnail
                                                    url={getFileUrl(data.certificate_url)}
                                                    alt="Certificate of Completion"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div style={{ display: 'grid', placeItems: 'center', height: '100%', color: 'var(--text-3)' }}>
                                                    Certificate Image Preview
                                                </div>
                                            )}
                                        </div>

                                        {/* Enlarge Badge Button */}
                                        <div className="cert-enlarge-badge" aria-hidden="true">
                                            <ZoomIn size={13} />
                                            <span>Click to enlarge</span>
                                        </div>
                                    </div>

                                    <div className="cert-meta">
                                        <div>
                                            <h3>Certificate of completion</h3>
                                            <p>AI Impact Fellowship</p>
                                        </div>
                                        <div className="icon-row">
                                            <button 
                                                className="icon-btn" 
                                                onClick={() => openInNewTab(data.certificate_url || data.certificate_image_url)}
                                                aria-label="Open certificate in new tab"
                                                title="Open in new tab"
                                            >
                                                <ExternalLink size={18} />
                                            </button>
                                            {data.certificate_url && (
                                                <a 
                                                    className="icon-btn" 
                                                    href={getFileUrl(data.certificate_url)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    download
                                                    aria-label="Download certificate"
                                                    title="Download certificate"
                                                >
                                                    <Download size={18} />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </section>

                                {/* Capstone Project Panel */}
                                <section className="panel project reveal in" aria-labelledby="projHead">
                                    <div>
                                        <div className="panel-head" style={{ marginBottom: 14 }}>
                                            <div>
                                                <h2 id="projHead">Capstone project</h2>
                                                <p>Built and presented during the fellowship</p>
                                            </div>
                                        </div>

                                        <h3 className="title">
                                            {cleanProjectTitle(data.project?.project_title)}
                                        </h3>
                                        <p className="desc">
                                            {data.project?.project_description || `${data.student_name} completed the capstone curriculum and presented a working solution at the fellowship Demo Day.`}
                                        </p>

                                        <dl className="kv">
                                            {(data.school_name || data.project?.school_name) && (
                                                <div>
                                                    <dt>School</dt>
                                                    <dd>{data.school_name || data.project?.school_name}</dd>
                                                </div>
                                            )}
                                            <div>
                                                <dt>Problem area</dt>
                                                <dd>{(data.project as any)?.problem_area || 'Education'}</dd>
                                            </div>
                                            <div>
                                                <dt>Presented at</dt>
                                                <dd>Demo Day, 12/09/2026</dd>
                                            </div>
                                            <div>
                                                <dt>Tools used</dt>
                                                <dd className="tags">
                                                    <span className="chip">Python</span>
                                                    <span className="chip">Generative AI</span>
                                                    <span className="chip">No-code</span>
                                                </dd>
                                            </div>
                                        </dl>
                                    </div>

                                    {/* Demo Presentation / Deck Preview or Empty State */}
                                    {data.project?.demo_presentation_path || data.project?.project_screenshot_path ? (
                                        <div className="slide-stage">
                                            <div className="slide-toolbar">
                                                <button
                                                    type="button"
                                                    onClick={() => openInNewTab(data.project!.demo_presentation_path || data.project!.project_screenshot_path!)}
                                                    className="slide-open-btn"
                                                >
                                                    <span>Open in new tab</span>
                                                    <ExternalLink size={14} />
                                                </button>
                                                <span 
                                                    className="slide-filename"
                                                    title={data.project.demo_presentation_name || data.project.project_screenshot_name || 'Capstone-Presentation.pdf'}
                                                >
                                                    {data.project.demo_presentation_name || data.project.project_screenshot_name || 'Capstone-Presentation.pdf'}
                                                </span>
                                            </div>
                                            <div 
                                                onClick={() => openInNewTab(data.project!.demo_presentation_path || data.project!.project_screenshot_path!)}
                                                style={{ aspectRatio: '16/9', width: '100%', background: '#000', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
                                            >
                                                {data.project.project_screenshot_path ? (
                                                    <img 
                                                        src={getFileUrl(data.project.project_screenshot_path)} 
                                                        alt="Project Presentation Preview" 
                                                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                    />
                                                ) : (
                                                    <PdfThumbnail 
                                                        url={getFileUrl(data.project.demo_presentation_path!)} 
                                                        alt="Presentation Deck"
                                                        className="w-full h-full object-contain"
                                                        scale={2}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="empty">
                                            <div className="ico">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="4" width="18" height="13" rx="2"/>
                                                    <path d="M8 21h8M12 17v4M10 8.5v5l4-2.5z"/>
                                                </svg>
                                            </div>
                                            <h3>Demo presentation not added yet</h3>
                                            <p>The fellow can upload slides or a screenshot from their student portal.</p>
                                        </div>
                                    )}
                                </section>
                            </div>
                        </>
                    )}
                </main>

                {/* ================= FOOTER ================= */}
                <footer className="site-footer">
                    <div className="wrap foot">
                        <div className="foot-brand">
                            <img className="foot-tpa" src={Images.tpaRealLogo} alt="The Purpose Academy" />
                            <span className="foot-div" aria-hidden="true" />
                            <img className="foot-omb" src={Images.onembillion} alt="1M1B Organization" />
                        </div>
                        <div className="foot-copy">
                            &copy; {new Date().getFullYear()} The Purpose Academy &bull; 1M1B Organization. All rights reserved.
                        </div>
                    </div>
                </footer>

                {/* ================= CERTIFICATE PREVIEW MODAL ================= */}
                <div 
                    className={`modal cert-modal-view ${modalOpen ? 'open' : ''}`}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setModalOpen(false);
                    }}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Certificate preview enlarged"
                >
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button 
                            type="button"
                            className="modal-close" 
                            onClick={() => setModalOpen(false)}
                            aria-label="Close modal"
                        >
                            <X size={20} />
                        </button>
                        {!certImgFailed && data?.certificate_image_url ? (
                            <img 
                                src={getFileUrl(data.certificate_image_url)} 
                                alt="Certificate of Completion Preview" 
                                onError={() => setCertImgFailed(true)}
                            />
                        ) : data?.certificate_url ? (
                            <div style={{ width: '100%', height: '80vh', background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
                                <iframe 
                                    src={getFileUrl(data.certificate_url)} 
                                    title="Certificate Preview"
                                    style={{ width: '100%', height: '100%', border: 'none' }}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>

                {/* ================= SHARE TO LINKEDIN MODAL ================= */}
                {shareModalOpen && (
                    <div 
                        className="share-modal-overlay"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) setShareModalOpen(false);
                        }}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="share-modal-card" onClick={(e) => e.stopPropagation()}>
                            <div className="share-modal-header">
                                <h3>
                                    <Sparkles size={18} style={{ color: '#f59e0b' }} />
                                    Share Achievement to LinkedIn
                                </h3>
                                <button 
                                    type="button" 
                                    className="share-modal-close-btn"
                                    onClick={() => setShareModalOpen(false)}
                                    aria-label="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                                Post your verified AI Fellowship credential directly to your LinkedIn feed. Download your certificate image and presentation screenshot to attach for high engagement.
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#0A66C2' }}>
                                        Suggested Post Caption
                                    </span>
                                    <button
                                        type="button"
                                        onClick={handleCopyCaption}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#0A66C2',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        {captionCopied ? <Check size={14} style={{ color: '#10b981' }} /> : <Copy size={14} />}
                                        {captionCopied ? 'Copied!' : 'Copy Caption'}
                                    </button>
                                </div>

                                {loadingLinkedInPost ? (
                                    <div style={{ padding: '24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-2)' }}>
                                        <Loader2 size={22} className="animate-spin" style={{ color: '#0A66C2' }} />
                                        <span>Preparing your personalized post caption...</span>
                                    </div>
                                ) : (
                                    <div className="share-ai-box">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {getSuggestedCaption()}
                                        </ReactMarkdown>
                                    </div>
                                )}
                            </div>

                            <div className="share-actions-grid">
                                {data?.certificate_image_url || data?.certificate_url ? (
                                    <a
                                        href={getFileUrl(data.certificate_image_url || data.certificate_url)}
                                        download
                                        target="_blank"
                                        rel="noreferrer"
                                        className="share-btn-secondary"
                                    >
                                        <ImageIcon size={15} style={{ color: '#0A66C2' }} />
                                        <span>Download Certificate</span>
                                    </a>
                                ) : null}

                                {data?.project?.project_screenshot_path ? (
                                    <button
                                        type="button"
                                        onClick={handleDownloadPresentationScreenshot}
                                        disabled={downloadingScreenshot}
                                        className="share-btn-secondary"
                                    >
                                        <Download size={15} style={{ color: '#9333ea' }} />
                                        <span>{downloadingScreenshot ? 'Downloading...' : 'Project Screenshot'}</span>
                                    </button>
                                ) : null}
                            </div>

                            <button
                                type="button"
                                className="share-btn-primary"
                                onClick={handleShareLinkedInPost}
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                    <path d="M4.98 3.5a2.5 2.5 0 11-.01 5 2.5 2.5 0 01.01-5zM3 9.5h4V21H3zM9.5 9.5h3.8v1.6h.1c.5-1 1.8-2 3.8-2 4 0 4.8 2.6 4.8 6V21h-4v-5.2c0-1.2 0-2.8-1.7-2.8s-2 1.3-2 2.7V21h-4z"/>
                                </svg>
                                <span>Open LinkedIn to Post</span>
                                <ExternalLink size={14} />
                            </button>
                        </div>
                    </div>
                )}

                {/* ================= FLOATING TOAST ================= */}
                <div className={`toast ${toastMsg ? 'show' : ''}`} role="status">
                    {toastMsg}
                </div>
            </div>
        </>
    );
};

export default VerifyCertificatePage;
